import { useEffect, useRef } from 'react'
import { useOptionalPageToolsContext } from '../pageTools/PageToolsProvider'
import { VoiceUITool } from './types'
import { voiceToolSurface } from './toolSurface'
import { toolSurfaceOf } from './uiBridge'

interface UseVoiceRegistrationOptions {
  /** Stable per-view identity, e.g. `tables:${customer}:${job}:${table}`. */
  pageKey: string
  /** Voice tools this view exposes. Always run as of the latest render. */
  uiTools: VoiceUITool[]
  /** Current page-context string. Read live (ref-backed) — always fresh. */
  pageContext: () => string
  /**
   * Default true. False registers nothing: a component drawn in more than one
   * place (SpecEditor: the /editspec page and the dashboard's Edit Data
   * overlay) registers only where it is the page, since a registration
   * replaces the page's slot.
   */
  enabled?: boolean
}

// Registers the current view's voice tools + context with the app-scoped page-tools
// provider for as long as the view is mounted.
//
// Two things used to be fixed at the render that set pageKey, and Douglas
// could not use most pages because of it (11 Sep): a page's tools close over
// state that loads after that render (a page list, a file list, a canSave
// flag), so they ran against the empty first render forever. Now:
// - execute always runs the latest render's tool of that name, through a ref;
// - the page re-registers when what the model sees changes (an option list
//   that has loaded), so the session gets the new schemas.
// pageContext is read through a ref so context stays current without
// re-registering.
//
// Search-only tools (#1534) are not in the model's list, so a change to them
// does not re-register: ui_search reads them live through liveTools.
export function useVoiceRegistration({ pageKey, uiTools, pageContext, enabled = true }: UseVoiceRegistrationOptions) {
  // No provider (a test tree, a page drawn outside the app shell): register
  // nothing rather than throw. The page still renders; Douglas is simply absent.
  const ctx = useOptionalPageToolsContext()
  const registerPage = ctx?.registerPage
  const unregisterPage = ctx?.unregisterPage

  const pageContextRef = useRef(pageContext)
  pageContextRef.current = pageContext
  const uiToolsRef = useRef(uiTools)
  uiToolsRef.current = uiTools
  const surface = voiceToolSurface(uiTools.filter((t) => toolSurfaceOf(t) === 'direct'))

  useEffect(() => {
    if (!enabled || !registerPage || !unregisterPage) return
    registerPage({
      pageKey,
      uiTools: uiToolsRef.current.map((t) => ({
        ...t,
        execute: (args: Record<string, unknown>) => {
          const live = uiToolsRef.current.find((x) => x.name === t.name)
          return live ? live.execute(args) : JSON.stringify({ error: `${t.name} is not available on this page right now` })
        },
      })),
      pageContext: () => pageContextRef.current(),
      liveTools: () => uiToolsRef.current,
    })
    return () => unregisterPage(pageKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageKey, surface, enabled])
}
